import { isWhatsappExtensionContext } from './detect-context';

export const RTL_LANGUAGES = ['ar', 'he'] as const;

export type AppDirection = 'ltr' | 'rtl';

export const normalizeLanguageTag = (language: string | null | undefined): string => {
  if (!language) return 'en';
  return language.split('-')[0].toLowerCase();
};

export const isRtlLanguage = (language: string | null | undefined): boolean => {
  const normalized = normalizeLanguageTag(language);
  return RTL_LANGUAGES.includes(normalized as (typeof RTL_LANGUAGES)[number]);
};

export const getDirectionForLanguage = (language: string | null | undefined): AppDirection => {
  return isRtlLanguage(language) ? 'rtl' : 'ltr';
};

/**
 * Syncs DOM direction attributes and rtl helper class from the active language.
 * Safe in non-DOM contexts (returns direction without mutating).
 */
export const applyDocumentDirection = (language: string | null | undefined): AppDirection => {
  const normalizedLanguage = normalizeLanguageTag(language);
  const direction = getDirectionForLanguage(normalizedLanguage);

  if (typeof document === 'undefined') {
    return direction;
  }

  // In WhatsApp Web content-script context, never mutate host page direction.
  // We still return the computed direction so extension state/components can use it.
  if (isWhatsappExtensionContext()) {
    return direction;
  }

  const htmlElement = document.documentElement;
  htmlElement.setAttribute('dir', direction);
  htmlElement.setAttribute('lang', normalizedLanguage);

  const syncBodyDirection = () => {
    if (!document.body) return;
    document.body.setAttribute('dir', direction);
    document.body.classList.toggle('rtl-mode', direction === 'rtl');
  };

  if (document.body) {
    syncBodyDirection();
  } else {
    document.addEventListener('DOMContentLoaded', syncBodyDirection, { once: true });
  }

  // Keep Crisp widget side in sync on every language direction update.
  try {
    (window as any).$crisp?.push(["config", "position:reverse", [direction === 'rtl']]);
  } catch {
    // Crisp may be unavailable in some contexts.
  }

  return direction;
};
